import React, { useState, useEffect } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import ArchiveIcon from '@mui/icons-material/Archive';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ReportIcon from '@mui/icons-material/Report';
import { Box, FormControlLabel, Grid, TextField, Tooltip } from '@mui/material';
import { format } from 'date-fns';
import Dialog from '@mui/material/Dialog';
import Checkbox from '@mui/material/Checkbox';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import './application.css';
import { useAppContext } from '../../../utils/content.util';
import { onError } from '../../../utils/error.util';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import TablePagination from '@mui/material/TablePagination';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import Paper from '@mui/material/Paper';
import { archiveApplication, getApplications, unarchiveApplication } from '../../../services/applications.service';
import Button from '@mui/material/Button';
import { Application } from '../../../models/application.model';
import { CircularProgressComponent } from '../../../components/admin/circular-progress.component';
import SearchBox from '../../../components/admin/search-box/search-box.component';
import { hasPermission, Permission } from '../../../models/role.model';
import { AwsCognitoUser } from '../../../models/user.model';
import { CompanyConfig } from '../../../company.config';

function ApplicationTable({
  data,
  page,
  rowsPerPage = 300,
  handleShowArchiveModal,
  handleShowUnarchiveModal,
  handleChangeRowsPerPage,
  handleChangePage,
  currentUser,
}: any) {
  return (
    <React.Fragment>
      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table sx={{ minWidth: 1200 }}>
          <TableHead>
            <TableRow>
              <TableCell> </TableCell>
              <TableCell>Viewed</TableCell>
              <TableCell>Analyst</TableCell>
              <TableCell>Source</TableCell>
              <TableCell>Stamp</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Location</TableCell>
              <TableCell> </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: any) => (
              <ApplicationTableRow
                key={row.id}
                row={row}
                handleShowArchiveModal={handleShowArchiveModal}
                handleShowUnarchiveModal={handleShowUnarchiveModal}
                currentUser={currentUser}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[300]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </React.Fragment>
  );
}

function showArchiveButton(user: AwsCognitoUser) {
  return hasPermission(user, Permission.ARCHIVE_APPLICATION);
}

interface ApplicationTableRowProps {
  row: Application;
  handleShowArchiveModal: any;
  handleShowUnarchiveModal: any;
  currentUser: any;
}

function ApplicationTableRow({
  row,
  handleShowArchiveModal,
  handleShowUnarchiveModal,
  currentUser,
}: ApplicationTableRowProps) {
  const [showAlerts, setShowAlerts] = useState(false);
  const [alertTitle, setAlertTitle] = useState('');

  useEffect(() => {
    if (row.alerts && row.alerts.length > 0) {
      setShowAlerts(true);
      setAlertTitle(
        row.alerts
          .map((alert: any) => {
            const timestamp = new Date(alert.alertTimestamp).toLocaleString();
            // return `[${timestamp}] ${alert.alertType} (${alert.alertSeverity}): ${alert.alertMessage}`;
            return `[${timestamp}]: ${alert.alertMessage}`;
          })
          .join('\n')
      );
    }
  }, [row.alerts]);

  return (
    <TableRow selected={row.isArchived}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Tooltip title="View">
            <ReactRouterLink key={row.id} to={`/service-forms/${row.id}`}>
              <VisibilityIcon color="primary" fontSize="small" />
            </ReactRouterLink>
          </Tooltip>
          {showAlerts && (
            <Tooltip title={<div style={{ whiteSpace: 'pre-line' }}>{alertTitle}</div>}>
              <ReportIcon color="error" fontSize="small" />
            </Tooltip>
          )}
        </Box>
      </TableCell>
      <TableCell>{row.viewedBy}</TableCell>
      <TableCell>{row.analystName}</TableCell>
      <TableCell>
        {row.isPhoneApplication ? (
          'Phone'
        ) : row.partner ? (
          <>
            Partner
            <br />
            {row.partner}
          </>
        ) : row.dealerId || row.dealerName ? (
          <>
            Dealer
            <br />
            {row.dealerName || 'N/A'} - {row.dealerId || 'N/A'}
          </>
        ) : row.haveCoApplicant ? (
          'CoBuyer'
        ) : row.referralSource === 'Lead' || row.referralSource === 'lead' ? (
          'Lead'
        ) : (
          CompanyConfig.shortName
        )}
      </TableCell>
      <TableCell>
        {format(row.createdAt!.toISOString(), 'MM-dd-yyyy')}
        <br />
        {format(row.createdAt!.toISOString(), 'p')}
      </TableCell>
      <TableCell>{`${row.firstName} ${row.lastName}`}</TableCell>
      <TableCell>{`${row.residentialCity}, ${row.residentialState}`}</TableCell>

      <TableCell align="left">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {!row.isArchived && currentUser && showArchiveButton(currentUser) ? (
            <Tooltip title="Archive">
              <ReactRouterLink onClick={() => handleShowArchiveModal(row.id)} to="">
                <ArchiveIcon color="primary" fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          ) : undefined}
          {row.isArchived && currentUser && showArchiveButton(currentUser) ? (
            <Tooltip title="Unarchive">
              <ReactRouterLink onClick={() => handleShowUnarchiveModal(row.id)} to="">
                <UnarchiveIcon color="primary" fontSize="small" />
              </ReactRouterLink>
            </Tooltip>
          ) : undefined}
        </Box>
      </TableCell>
    </TableRow>
  );
}

export default function ApplicationsContainer() {
  const { isAuthenticated, currentUser } = useAppContext();

  const [applications, setApplications] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isUnarchiving, setIsUnarchiving] = useState(false);
  const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
  const [showUnarchiveModal, setShowUnarchiveModal] = useState<boolean>(false);
  const [currentItemId, setCurrentItemId] = useState<string>('');

  const [searchStartDate, setSearchStartDateValue] = useState('');
  const [searchEndDate, setSearchEndDateValue] = useState('');
  const [showArchivedApplications, setShowArchivedApplications] = useState(false);
  const [showPhoneApplications, setShowPhoneApplications] = useState(false);
  const [shouldReload, setShouldReload] = useState(false);

  const handleClickOpenArchive = (itemId: string) => {
    setCurrentItemId(itemId);
    setShowArchiveModal(true);
  };

  const handleClickOpenUnarchive = (itemId: string) => {
    setCurrentItemId(itemId);
    setShowUnarchiveModal(true);
  };

  const handleCloseArchive = () => setShowArchiveModal(false);
  const handleCloseUnarchive = () => setShowUnarchiveModal(false);

  const handleArchive = async () => {
    setIsArchiving(true);
    try {
      await archiveApplication(currentItemId);
    } catch (err) {
      alert(err);
    } finally {
      setShowArchiveModal(false);
      setIsArchiving(false);
      onLoad();
    }
  };

  const handleUnarchive = async () => {
    setIsUnarchiving(true);
    try {
      await unarchiveApplication(currentItemId);
    } catch (err) {
      alert(err);
    } finally {
      setShowUnarchiveModal(false);
      setIsUnarchiving(false);
      onLoad();
    }
  };

  async function onLoad(name = '') {
    if (!isAuthenticated) {
      return;
    }

    setIsLoading(true);
    setApplications([]);

    try {
      const applications = await getApplications({
        startDate: searchStartDate,
        endDate: searchEndDate,
        name,
        archived: showArchivedApplications,
        isPhoneApplication: showPhoneApplications,
      });
      setApplications(applications);
    } catch (e) {
      onError(e);
    }

    setIsLoading(false);
  }

  useEffect(() => {
    onLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (search: string) => {
    onLoad(search);
  };

  useEffect(() => {
    if (shouldReload) {
      onLoad();
      setShouldReload(false); // Reset the flag
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldReload]);

  const handleSearchClear = () => {
    setSearchStartDateValue('');
    setSearchEndDateValue('');
    // setSearchNameValue("");
    setShowArchivedApplications(false);
    setShowPhoneApplications(false);
    setShouldReload(true); // Set the flag to true
  };

  function renderApplications() {
    return (
      <>
        <div className="applications">
          <h2 className="pb-2 mt-4 mb-3 border-bottom">Service Forms</h2>
        </div>
        <div className="pb-2 mt-4 mb-3">
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 1 }}>
            {/* Date input for start date */}
            <TextField
              label="Start Date"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={searchStartDate}
              onChange={(e) => setSearchStartDateValue(e.target.value)}
            />

            {/* Date input for end date */}
            <TextField
              label="End Date"
              type="date"
              slotProps={{ inputLabel: { shrink: true } }}
              value={searchEndDate}
              onChange={(e) => setSearchEndDateValue(e.target.value)}
            />

            <SearchBox placeholder="Buyer name" handleSubmit={handleSearch} handleClearAll={handleSearchClear} />

            <FormControlLabel
              control={<Checkbox />}
              label="Archived"
              checked={showArchivedApplications}
              onChange={() => setShowArchivedApplications(!showArchivedApplications)}
            />

            <FormControlLabel
              control={<Checkbox />}
              label="Phone"
              checked={showPhoneApplications}
              onChange={() => setShowPhoneApplications(!showPhoneApplications)}
            />
          </Box>
        </div>

        <div className="pb-2 mt-4 mb-3"></div>
      </>
    );
  }

  return (
    <React.Fragment>
      <Grid container size={12} sx={{ width: '100%' }}>
        <div className="Applications" style={{ width: '100%' }}>
          {renderApplications()}
          {isLoading ? (
            <CircularProgressComponent />
          ) : (
            <ApplicationTable
              data={applications}
              page={0}
              rowsPerPage={300}
              handleShowArchiveModal={handleClickOpenArchive}
              handleShowUnarchiveModal={handleClickOpenUnarchive}
              handleChangeRowsPerPage={() => {}}
              handleChangePage={() => {}}
              currentUser={currentUser}
            />
          )}
        </div>
      </Grid>

      <Dialog
        open={showArchiveModal}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          handleCloseArchive();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to archive this?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleArchive} disabled={isArchiving}>
            Archive
          </Button>
          <Button onClick={handleCloseArchive} disabled={isArchiving} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showUnarchiveModal}
        onClose={(_event, reason) => {
          if (reason === 'escapeKeyDown') return;
          handleCloseUnarchive();
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">Confirmation</DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">Are you sure you want to unarchive this?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUnarchive} disabled={isUnarchiving}>
            Unarchive
          </Button>
          <Button onClick={handleCloseUnarchive} disabled={isUnarchiving} autoFocus>
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
}
